/**
 * locale.ts — language (UI locale) axis for the app.
 *
 * IMPORTANT: language is INDEPENDENT of country. Country (US/CA/NZ/AU/AR/CL)
 * selects which country's campsites to show; language (en/es) selects the UI
 * chrome language. A visitor browsing Argentina may want the UI in English or
 * Spanish — the two never derive from each other.
 *
 * Phase 1 uses next-intl in "no i18n routing" mode: the active locale is driven
 * by the NEXT_LOCALE cookie (see i18n/request.ts) and URLs stay stable, so the
 * existing [country] routing + SEO surfaces are undisturbed. Locale-prefixed,
 * SEO-indexable Spanish URLs + hreflang are a deliberate PHASE 2 follow-up.
 *
 * The language switcher is gated to the escapeto brand (see isLanguageEnabled)
 * — camping-australia stays English-only and its behaviour is unchanged.
 */
import { BRAND } from './brand';

export type Locale = 'en' | 'es';

/** All locales the UI ships message catalogues for. */
export const LOCALES: readonly Locale[] = ['en', 'es'] as const;

/** Default locale when no cookie is present (or for camping-australia). */
export const DEFAULT_LOCALE: Locale = 'en';

/** Cookie that remembers the visitor's preferred UI language. */
export const LOCALE_COOKIE = 'NEXT_LOCALE';

/** Narrow an arbitrary string to a supported Locale, or null. */
export function toLocale(value?: string | null): Locale | null {
  return value && (LOCALES as readonly string[]).includes(value) ? (value as Locale) : null;
}

/**
 * Whether the language switcher (and non-English locales) are available.
 * escapeto only — it serves the international / South American market. On
 * camping-australia this is false, so the UI is English-only and unchanged.
 */
export function isLanguageEnabled(): boolean {
  return BRAND.key === 'escapeto';
}
